import isNil from 'lodash/isNil';
import {
  isNilOrZero,
} from '@populateui/utils';
import {
  ChartTab,
  isValidChartTab,
} from 'pages/Dashboard/pages/Charts/utils';
import type {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  PanelDefinition,
  POSITIVE_INTEGER,
} from 'pages/Dashboard/contexts/sidePanel/panelUrl';

export type ChargesPanelTab = 'charge' | 'invoice' | 'chart';

export const CHARGES_DEFAULT_TAB: ChargesPanelTab = 'charge';

/**
 * A missing `tab` means Charge. `chartTab` is only kept while `tab` is
 * `chart`. A missing `claimId` means no claim is pre-selected.
 */
export type ChargesPanelInfo = {
  encounterId: number;
  patientId: number;
  // TODO: read visit date from the encounter summary once the API returns it, then drop the visitDate URL param
  visitDate?: string;
  claimId?: number;
  tab?: ChargesPanelTab;
  chartTab?: string;
};

// Props of the component shown for each top tab
export type ChargesTabProps = SidePanelComponentProps & {
  selectedClaimId?: number;
  onSelectedClaimChange?: (claimId?: number) => void;
};

const CHARGES_PANEL_TABS: readonly string[] = ['charge', 'invoice', 'chart'];

function isChargesPanelTab(value: unknown): value is ChargesPanelTab {
  return typeof value === 'string' && CHARGES_PANEL_TABS.includes(value);
}

type ChargesPanelOpener = {
  encounterId?: number | null;
  patientId?: number | null;
  visitDate?: string | null;
  claimId?: number | null;
  tab?: ChargesPanelTab;
  chartTab?: string;
};

/**
 * Builds the panel info from a row's values: no claim for a missing or zero
 * claim id, and the chart tab only on the Chart tab.
 */
export function toChargesPanelInfo({
  encounterId,
  patientId,
  visitDate,
  claimId,
  tab,
  chartTab,
}: ChargesPanelOpener): ChargesPanelInfo {
  return {
    encounterId: Number(encounterId ?? 0),
    patientId: Number(patientId ?? 0),
    visitDate: visitDate ?? undefined,
    claimId: isNilOrZero(claimId) ? undefined : Number(claimId),
    tab,
    chartTab: tab === 'chart' ? chartTab : undefined,
  };
}

/**
 * The claim to select after an `onChargesInfoChange` payload: its claim `id`,
 * none for `id: 0`, or the current one when it has no `id`.
 */
export function nextClaimSelection(
  current: number | undefined,
  payload: { id?: number | string | null },
): number | undefined {
  if (isNil(payload.id)) {
    return current;
  }

  return isNilOrZero(Number(payload.id)) ? undefined : Number(payload.id);
}

export function resolveChartTab(
  chartTab: string | undefined,
  { defaultChartTab, canChartTab }: {
    defaultChartTab: ChartTab;
    canChartTab: (tab: ChartTab) => boolean;
  },
): ChartTab {
  return isValidChartTab(chartTab) && canChartTab(chartTab) ? chartTab : defaultChartTab;
}

export function parseChargesTab(raw: string): ChargesPanelTab | null {
  return isChargesPanelTab(raw) ? raw : null;
}

export function parseChartTab(raw: string): ChartTab | null {
  return isValidChartTab(raw) ? raw : null;
}

export function parseClaimId(raw: string): number | null {
  return POSITIVE_INTEGER.test(raw) ? Number(raw) : null;
}

export function getChargesPanelIdentity({ encounterId }: ChargesPanelInfo) {
  return String(encounterId);
}

export const chargesPanelUrl: NonNullable<PanelDefinition<ChargesPanelInfo>['url']> = {
  // TODO: read visit date from the encounter summary once the API returns it, then drop the visitDate URL param
  params: ['encounterId', 'patientId', 'visitDate', 'claimId', 'tab', 'chartTab'],
  serialize: ({
    encounterId,
    patientId,
    visitDate,
    claimId,
    tab,
    chartTab,
  }) => ({
    encounterId: String(encounterId),
    patientId: String(patientId),
    // Written exactly as the opener gave it
    ...(isNil(visitDate) ? {} : { visitDate }),
    ...(isNilOrZero(claimId) ? {} : { claimId: String(claimId) }),
    ...(isNil(tab) || tab === CHARGES_DEFAULT_TAB ? {} : { tab }),
    ...(tab === 'chart' && !isNil(chartTab) ? { chartTab } : {}),
  }),
  // Encounter and patient are required. Visit date may be missing; a bad
  // claim, tab or chart tab falls back to none or the default.
  parse: (searchParams) => {
    const encounterId = searchParams.get('encounterId');
    const patientId = searchParams.get('patientId');

    if (isNil(encounterId) || !POSITIVE_INTEGER.test(encounterId)
      || isNil(patientId) || !POSITIVE_INTEGER.test(patientId)) {
      return null;
    }

    const visitDate = searchParams.get('visitDate');
    const claimId = searchParams.get('claimId');
    const tab = searchParams.get('tab');
    const chartTab = searchParams.get('chartTab');
    const parsedTab = isNil(tab) ? null : parseChargesTab(tab);
    const parsedClaimId = isNil(claimId) ? null : parseClaimId(claimId);
    const parsedChartTab = parsedTab === 'chart' && !isNil(chartTab) ? parseChartTab(chartTab) : null;

    return {
      encounterId: Number(encounterId),
      patientId: Number(patientId),
      ...(isNil(visitDate) ? {} : { visitDate }),
      ...(isNil(parsedClaimId) ? {} : { claimId: parsedClaimId }),
      ...(isNil(parsedTab) || parsedTab === CHARGES_DEFAULT_TAB ? {} : { tab: parsedTab }),
      ...(isNil(parsedChartTab) ? {} : { chartTab: parsedChartTab }),
    };
  },
};
